import { Interaction as ApiInteraction, Conversation } from '@quadminds/client-apis/agents';
import { useToast } from '@quadminds/mindkit';
import { IAuthMeV2OutputDto } from '@quadminds/shared/auth-system';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { useCombinedSuggestedQuestions } from './useFavoriteQuestions';
import { useFixedToParent } from './useFixedToParent';
import { useGetConversationBy } from './useGetConversationBy';
import { useMindyViewContext } from './useMindyViewContext';
import { useUserPlatform } from './usePlatform';
import { useSendKnowledgeMessage } from './useSendKnowledgeMessage';
import { useSendLogisticsMessage } from './useSendLogisticsMessage';
import { useSSE, SSEData } from './useSSE';
import { useUploadFile } from './useUploadFile';
import { UploadedFile } from '../components/pages/Chat/Input/Input';
import { buildChatSuggestedQuestions, resolveChatSuggestedQuestionsProfile } from '../config/chatSuggestedQuestions';
import { CHAT_MODE } from '../enums/chatMode.enum';
import { UserPlatformEnum } from '../enums/platform.enum';
import { SENDER } from '../enums/sender.enum';
import { SOURCE } from '../enums/source.enum';
import { AgentObjects, IOptimisticAgentResponse, SuggestedQuestion } from '../interfaces/chat.interface';
import { fromInteractionToConversationType } from '../mappers/conversation/conversation';
import { fromMessageModeToMessageOutput } from '../mappers/message/message';
import { useByPassContext } from '../providers/ByPass/ByPass.provider';
import { useChatContext } from '../providers/Chat/Chat.provider';
import { useProviderContext } from '../providers/Firebase/Firebase.provider';
import { chatModeType } from '../types/Chat/Chat.types';
import { ConversationType } from '../types/Conversation/Conversation';
import { UploadDestination } from '../types/File/File.types';
import { getConversationMode, shouldShowAgentVersionMismatch } from '../utils/chatConversation';
import { resolveComposerMode } from '../utils/chatModePolicy';
import { isKnowledgeUser, getDefaultAgentVersion } from '../utils/utils';

const KNOWLEDGE_THINKING_STAGES = [
    { minSeconds: 0, message: 'Analizando tu consulta...' },
    { minSeconds: 3, message: 'Consultando base de conocimiento...' },
    { minSeconds: 10, message: 'Estructurando la respuesta...' },
    { minSeconds: 15, message: 'Generando respuesta final...' },
    { minSeconds: 30, message: 'Esta respuesta está tardando más de lo esperado...' },
];

const useChat = () => {
    const [chatInputValue, setChatInputValue] = useState('');
    const [knowledgeEmbeddingDestination, setKnowledgeEmbeddingDestination] = useState<UploadDestination>(
        UploadDestination.STANDARD,
    );
    const [files, setFiles] = useState<UploadedFile[]>([]);
    const [messages, setMessages] = useState<ConversationType[]>([]);
    const [showDisclaimer, setShowDisclaimer] = useState(false);
    const [showRestScrollButton, setShowRestScrollButton] = useState(false);
    const [buttonTop, setButtonTop] = useState<number | null>(null);
    const bottomRef = useRef<HTMLDivElement>(null);
    const messagesListRef = useRef<HTMLDivElement>(null);
    const isStickyRef = useRef(true);
    // When true, suppresses ALL auto-scroll until user manually goes to bottom or response finishes
    const scrollLockRef = useRef(false);
    // Stores the target scroll Y position to keep the user anchor message visible
    const targetUserMessageScrollPos = useRef<number | null>(null);

    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const ref = useRef<HTMLDivElement>(null);
    const refFocusControl = useRef(false);
    const inputRef = useRef<{
        focusTextArea(): void;
        isTextAreaFocused(): boolean;
        isTextAreaDisabled(): boolean;
    } | null>(null);

    const { isImpersonated } = useByPassContext();
    const { userPlatform } = useUserPlatform();
    // Contexto de pantalla publicado por el SaaS (QS-19310). Ref, no state:
    // no debe disparar un re-render del chat en cada tick del polling del dashboard.
    const viewContextRef = useMindyViewContext();

    // Refs and Helpers moved to top to avoid used-before-defined errors
    const lastMessageTimeRef = useRef<number>(0);
    const messageQueueRef = useRef<Array<{ message?: string }>>([]);
    const processingQueueRef = useRef(false);

    const activeStreamIdRef = useRef<string | null>(null);
    const thinkingIdRef = useRef<string | null>(null);
    const streamBufferRef = useRef<string>('');
    const isStreamingRef = useRef(false);
    const activeKnowledgeRequestIdRef = useRef<string | null>(null);

    const knowledgeThinkingIdRef = useRef<string | null>(null);
    const knowledgeThinkingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const knowledgeThinkingStartTimeRef = useRef<number>(0);
    const knowledgeThinkingStageIndexRef = useRef<number>(-1);

    const generateLocalId = useCallback(() => {
        return uuidv4();
    }, []);

    const scrollToBottom = useCallback((behavior: ScrollBehavior = 'auto') => {
        requestAnimationFrame(() => {
            window.scrollTo({
                top: document.documentElement.scrollHeight,
                behavior,
            });
        });
    }, []);

    const releaseScrollLock = useCallback(() => {
        scrollLockRef.current = false;
        targetUserMessageScrollPos.current = null;
    }, []);

    const scrollToBottomAndUnlock = useCallback(
        (behavior: ScrollBehavior = 'smooth') => {
            releaseScrollLock();
            isStickyRef.current = true;
            scrollToBottom(behavior);
        },
        [scrollToBottom, releaseScrollLock],
    );

    const logisticsUrl =
        userPlatform === UserPlatformEnum.STORK ? '/v3/stork/assistant/stream' : '/v3/logistics/assistant/stream';
    const { data, startStream, isStreaming, clearData, cancelStream } = useSSE(logisticsUrl);
    isStreamingRef.current = isStreaming;

    const DEFAULT_QUESTIONS_QUANTITY = 4;
    const [firebaseIdToken, setFirebaseIdToken] = useState<string | null>(null);

    const chatContainerRef = useRef<HTMLDivElement>(null);
    const inputContainerRef = useRef<HTMLDivElement>(null);

    useFixedToParent(chatContainerRef, inputContainerRef);

    const { id: paramId } = useParams();
    const { toast, toasts } = useToast();

    const userInfo: IAuthMeV2OutputDto | undefined = queryClient.getQueryData(['userInfo']);
    const knowledgeUser = isKnowledgeUser(userInfo);
    // Retrieve userInfo from the cache
    const {
        mode,
        conversationId,
        reset,
        load,
        hasFile,
        setHasFile,
        setLoad,
        setReset,
        setConversationId,
        setIsPending,
        setIsStream,
        setMode,
    } = useChatContext(); // Access the chat context
    const { user } = useProviderContext();

    const currentOrganization = userInfo?.currentOrganization;
    const currentOrganizationSettings =
        currentOrganization && userInfo?.settings?.[currentOrganization]
            ? (userInfo.settings[currentOrganization] as Record<string, unknown>)
            : undefined;
    const showKnowledgeEmbeddingSelector =
        mode === CHAT_MODE.knowledge && Boolean(currentOrganizationSettings?.['mindy:upload_destination_selector']);
    const agentsData =
        currentOrganization && userInfo?.settings?.[currentOrganization]
            ? (userInfo.settings[currentOrganization] as Record<string, unknown>)?.['mindy:agents']
            : undefined;
    const availableUserAgents: AgentObjects[] = Array.isArray(agentsData) ? agentsData : [];

    // User preference store the user nickname for that organization
    const userNickname =
        currentOrganization && userInfo?.settings?.[currentOrganization]
            ? (userInfo.settings[currentOrganization] as Record<string, unknown>)?.['mindy:nickname']
            : undefined;

    const suggestedQuestionsProfile = resolveChatSuggestedQuestionsProfile({
        userNickname: typeof userNickname === 'string' ? userNickname : undefined,
        userPlatform,
    });

    const suggestedQuestionsTemplate = useMemo(
        () => buildChatSuggestedQuestions(t, suggestedQuestionsProfile.questions),
        [suggestedQuestionsProfile.questions, t],
    );

    // Extract dynamic suggested questions from user settings
    const suggestedQuestionsData =
        currentOrganization && userInfo?.settings?.[currentOrganization]
            ? (userInfo.settings[currentOrganization] as Record<string, unknown>)?.['mindy:suggested_questions']
            : undefined;
    const dynamicSuggestedQuestions: SuggestedQuestion[] = Array.isArray(suggestedQuestionsData)
        ? (suggestedQuestionsData as SuggestedQuestion[])
        : [];

    const suggestedQuestionsQuantity = Number(
        (currentOrganization &&
            (userInfo?.settings?.[currentOrganization] as Record<string, unknown> | undefined)?.[
                'mindy:suggested_questions_quantity'
            ]) ||
            DEFAULT_QUESTIONS_QUANTITY,
    );

    const agentVersion = ((currentOrganization && userInfo?.settings?.[currentOrganization]
        ? (userInfo.settings[currentOrganization] as Record<string, unknown>)?.['mindy:agent_version']
        : undefined) ?? getDefaultAgentVersion(userInfo)) as string;
    const conversation = queryClient.getQueryData<Conversation>(['conversation', conversationId]);

    const conversationMetadata = conversation?.metadata || {};
    const conversationAgentVersion = (conversationMetadata?.['agent_version'] ?? agentVersion) as string;

    const bgImageUrl =
        currentOrganization && userInfo?.settings?.[currentOrganization]
            ? (userInfo.settings[currentOrganization] as Record<string, unknown>)?.['mindy:bg_image_url']
            : undefined;
    const customBackgroundImage = typeof bgImageUrl === 'string' ? bgImageUrl : undefined;

    // Use new combined logic: favorites + suggested + default (max 4 total)
    const suggestedQuestions = useCombinedSuggestedQuestions(
        currentOrganization || '',
        suggestedQuestionsTemplate,
        dynamicSuggestedQuestions,
        {
            includeFavorites: suggestedQuestionsProfile.includeFavorites,
            includeDynamicQuestions: suggestedQuestionsProfile.includeDynamicQuestions,
        },
    );

    const agentIdToUse = knowledgeUser
        ? 'knowledge'
        : mode === CHAT_MODE.logistics
        ? availableUserAgents[0]?.id
        : 'knowledge';

    const filesRef = useRef(files);
    filesRef.current = files;

    const handleResetFinalAndInput = useCallback(() => {
        setChatInputValue('');
        setFiles([]);
        setHasFile(false);
    }, [setHasFile]);

    const handleOptimisticUserRequest = useCallback(
        (message: string) => {
            const currentFiles = filesRef.current;
            handleResetFinalAndInput();
            // Add user message optimistically (without interactionId until backend responds)
            setMessages((prevState) => [
                ...prevState,
                {
                    text: message,
                    sender: SENDER.user,
                    files: currentFiles,
                    messageType: 'user',
                },
            ]);
            refFocusControl.current = true;
        },
        [handleResetFinalAndInput],
    );

    const handleOptimisticAgentResponse = ({
        message,
        attachments,
        interactionId,
        rating,
        conversationId: newConversationId,
    }: IOptimisticAgentResponse) => {
        setMessages((prevState) => [
            ...prevState,
            {
                text: message,
                sender: SENDER.agent,
                attachments: attachments,
                interactionId,
                rating,
                animated: true,
                messageType: 'done',
            },
        ]);

        // Set conversation ID for new conversations
        if (newConversationId && !conversationId) {
            setConversationId(newConversationId);
        }

        activeKnowledgeRequestIdRef.current = null;
    };

    const stopKnowledgeThinking = useCallback(() => {
        if (knowledgeThinkingIntervalRef.current) {
            clearInterval(knowledgeThinkingIntervalRef.current);
            knowledgeThinkingIntervalRef.current = null;
        }
    }, []);

    const handleKnowledgeOptimisticRequest = useCallback(
        (message: string) => {
            stopKnowledgeThinking();
            const prevThinkingId = knowledgeThinkingIdRef.current;
            if (prevThinkingId) {
                setMessages((prev) => prev.filter((m) => m.localId !== prevThinkingId));
                knowledgeThinkingIdRef.current = null;
            }

            handleOptimisticUserRequest(message);

            const thinkingId = generateLocalId();
            knowledgeThinkingIdRef.current = thinkingId;
            knowledgeThinkingStartTimeRef.current = Date.now();

            setMessages((prev) => [
                ...prev,
                {
                    localId: thinkingId,
                    sender: SENDER.agent,
                    text: KNOWLEDGE_THINKING_STAGES[0].message,
                    animated: true,
                    messageType: 'thinking',
                },
            ]);

            if (isStickyRef.current) {
                scrollToBottom('smooth');
            }

            knowledgeThinkingStageIndexRef.current = 0;
            knowledgeThinkingIntervalRef.current = setInterval(() => {
                const elapsed = (Date.now() - knowledgeThinkingStartTimeRef.current) / 1000;
                let stageIndex = 0;
                for (let i = KNOWLEDGE_THINKING_STAGES.length - 1; i >= 0; i--) {
                    if (elapsed >= KNOWLEDGE_THINKING_STAGES[i].minSeconds) {
                        stageIndex = i;
                        break;
                    }
                }
                if (stageIndex === knowledgeThinkingStageIndexRef.current) return;
                knowledgeThinkingStageIndexRef.current = stageIndex;
                setMessages((prev) => {
                    const idx = prev.findIndex((m) => m.localId === knowledgeThinkingIdRef.current);
                    if (idx === -1) return prev;
                    const updated = [...prev];
                    updated[idx] = { ...updated[idx], text: KNOWLEDGE_THINKING_STAGES[stageIndex].message };
                    return updated;
                });
            }, 500);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [generateLocalId, handleOptimisticUserRequest, scrollToBottom, stopKnowledgeThinking],
    );

    const handleKnowledgeAgentResponse = useCallback(
        ({
            message,
            attachments,
            interactionId,
            rating,
            conversationId: newConversationId,
        }: IOptimisticAgentResponse) => {
            stopKnowledgeThinking();
            const thinkingId = knowledgeThinkingIdRef.current;
            knowledgeThinkingIdRef.current = null;

            setMessages((prev) => {
                const filtered = thinkingId ? prev.filter((m) => m.localId !== thinkingId) : prev;
                return [
                    ...filtered,
                    {
                        text: message,
                        sender: SENDER.agent,
                        attachments,
                        interactionId,
                        rating,
                        animated: true,
                        messageType: 'done',
                    },
                ];
            });

            if (newConversationId && !conversationId) {
                setConversationId(newConversationId);
            }

            activeKnowledgeRequestIdRef.current = null;
        },
        [conversationId, setConversationId, stopKnowledgeThinking],
    );

    const clearPendingThinkingMessages = useCallback(() => {
        stopKnowledgeThinking();

        knowledgeThinkingIdRef.current = null;
        knowledgeThinkingStageIndexRef.current = -1;
        knowledgeThinkingStartTimeRef.current = 0;

        setMessages((prev) =>
            prev.filter((message) => message.messageType !== 'thinking' || Boolean(message.interactionId)),
        );

        thinkingIdRef.current = null;
        activeStreamIdRef.current = null;
        streamBufferRef.current = '';
        releaseScrollLock();
    }, [releaseScrollLock, stopKnowledgeThinking]);

    const {
        data: loadedConversation,
        isPending,
        isError: isErroGettingConversation,
    } = useGetConversationBy(conversationId || paramId || '', {
        enabled: !!(conversationId || paramId) && !reset,
    });

    const loadedConversationMode = useMemo(
        () => getConversationMode(loadedConversation?.interactions),
        [loadedConversation?.interactions],
    );
    const cachedConversationMode = useMemo(
        () => getConversationMode(conversation?.interactions),
        [conversation?.interactions],
    );

    const {
        mutate: sendMessageToKnowledge,
        isPending: isLoadingKnowledgeResponse,
        activeRequestId: activeKnowledgeRequestId,
        cancelKnowledgeRequest,
    } = useSendKnowledgeMessage(handleKnowledgeOptimisticRequest, handleKnowledgeAgentResponse, () => {
        activeKnowledgeRequestIdRef.current = null;
        stopKnowledgeThinking();
    });

    const { mutate: sendMessageToLogistics, isPending: isLoadingLogisticsResponse } = useSendLogisticsMessage(
        handleOptimisticUserRequest,
        handleOptimisticAgentResponse,
    );

    const compareMessages = (loadedMessages: ConversationType[], prevMessages: ConversationType[]) => {
        // Single O(M) pass: build text lookup map + collect optimistic messages + count confirmed
        let confirmedCount = 0;
        const optimisticMessages: ConversationType[] = [];
        const prevByKey = new Map<string, ConversationType>();
        for (const p of prevMessages) {
            prevByKey.set(`${p.sender}::${p.text}`, p);
            if (p.interactionId) {
                confirmedCount++;
            } else {
                optimisticMessages.push(p);
            }
        }

        return loadedMessages.map((msg: ConversationType, index: number) => {
            // Primary: match by text+sender (exact)
            let prevMatch = prevByKey.get(`${msg.sender}::${msg.text}`);

            // Fallback: positional match for new messages beyond confirmed history
            // Handles the case where backend text differs slightly from the streaming buffer
            if (!prevMatch && index >= confirmedCount) {
                const candidate = optimisticMessages[index - confirmedCount];
                if (candidate?.sender === msg.sender) {
                    prevMatch = candidate;
                }
            }

            return {
                ...msg,
                // Preserve localId so React keys stay stable when backend data replaces optimistic state (avoids remount flash)
                localId: prevMatch?.localId ?? msg.localId,
                animated:
                    msg.sender === SENDER.agent && index === loadedMessages.length - 1
                        ? prevMatch?.animated ?? false
                        : msg.animated ?? false,
            };
        });
    };

    const { mutate: uploadFile, isPending: isUploadingFiles } = useUploadFile();

    const isActive = chatInputValue || files.length > 0;

    const onStreamMessage = useCallback(
        (data: SSEData) => {
            if (data.event === 'thinking' || (data.event === 'agent_switch' && data.message)) {
                if (!thinkingIdRef.current) {
                    thinkingIdRef.current = generateLocalId();
                }

                const thinkingId = thinkingIdRef.current;

                setMessages((prev) => {
                    const index = prev.findIndex((m) => m.localId === thinkingId);

                    if (index !== -1) {
                        const updated = [...prev];
                        updated[index] = {
                            ...updated[index],
                            text: data.message ?? '',
                            animated: true,
                            messageType: 'thinking',
                        };
                        return updated;
                    }

                    return [
                        ...prev,
                        {
                            localId: thinkingId,
                            sender: SENDER.agent,
                            text: data.message ?? '',
                            animated: true,
                            messageType: 'thinking',
                        },
                    ];
                });

                // Auto-scroll when "thinking" message appears only if user is near bottom
                if (isStickyRef.current) {
                    scrollToBottom('smooth');
                }

                return;
            }

            if (data.event === 'token' && data.content) {
                const tid = thinkingIdRef.current;
                if (tid) {
                    activeStreamIdRef.current = tid;
                    thinkingIdRef.current = null;
                    streamBufferRef.current = '';
                }

                if (!activeStreamIdRef.current) {
                    activeStreamIdRef.current = generateLocalId();
                    streamBufferRef.current = '';
                }

                const streamId = activeStreamIdRef.current;

                streamBufferRef.current += data.content;

                setMessages((prev) => {
                    const index = prev.findIndex((m) => m.localId === streamId);

                    if (index !== -1) {
                        const updated = [...prev];
                        updated[index] = {
                            ...updated[index],
                            text: streamBufferRef.current,
                            animated: true,
                            messageType: 'token',
                        };
                        return updated;
                    }

                    return [
                        ...prev,
                        {
                            localId: streamId,
                            sender: SENDER.agent,
                            text: streamBufferRef.current,
                            animated: true,
                            messageType: 'token',
                        },
                    ];
                });

                // Auto-scroll during streaming if user is near bottom
                if (isStickyRef.current) {
                    scrollToBottom('auto');
                }

                return;
            }

            if (data.event === 'done') {
                const streamId = activeStreamIdRef.current;

                // Release scroll lock when streaming finishes
                releaseScrollLock();

                const doneAttachments = data.metadata?.attachments;
                const defaultText = t('errors.mindyEmptyResponse');

                if (!streamId) {
                    // No tokens received — resolve any pending thinking message with a default response
                    const thinkingId = thinkingIdRef.current;

                    if (thinkingId) {
                        setMessages((prev) =>
                            prev.map((m) =>
                                m.localId === thinkingId
                                    ? { ...m, text: defaultText, messageType: 'done', animated: false }
                                    : m,
                            ),
                        );
                        thinkingIdRef.current = null;
                    } else {
                        setMessages((prev) => [
                            ...prev,
                            {
                                localId: generateLocalId(),
                                sender: SENDER.agent,
                                text: defaultText,
                                animated: false,
                                messageType: 'done',
                            },
                        ]);
                    }
                    return;
                }

                activeStreamIdRef.current = null;

                setMessages((prev) =>
                    prev.map((m) =>
                        m.localId === streamId
                            ? {
                                  ...m,
                                  text: streamBufferRef.current.trim() || defaultText,
                                  messageType: 'done',
                                  animated: false,
                                  ...(doneAttachments?.length ? { attachments: doneAttachments } : {}),
                              }
                            : m,
                    ),
                );
            }
        },
        [scrollToBottom, generateLocalId, releaseScrollLock, t],
    );

    const handleSendMessage = useCallback(
        ({ message, mode, source }: { message?: string; mode?: chatModeType; source?: SOURCE }) => {
            const hasNoInferredArgs = !chatInputValue.trim() && files.length === 0;
            const hasNoDeclaredArgs = message === undefined && mode === undefined;

            if (hasNoInferredArgs && hasNoDeclaredArgs) return;
            const currentMode = resolveComposerMode({
                userInfo,
                currentMode: mode ?? CHAT_MODE.logistics,
                hasFiles: files.length > 0,
            });

            if (files.length > 0) {
                uploadFile(
                    {
                        files: files.map((file) => file.file),
                        conversationId: conversationId,
                    },
                    {
                        onSuccess: (uploadedFiles) => {
                            if (uploadedFiles.conversation_id && conversationId === undefined) {
                                setConversationId(uploadedFiles.conversation_id);
                            }
                            if (!hasFile) {
                                setHasFile(true);
                            }
                            const requestId = generateLocalId();
                            activeKnowledgeRequestIdRef.current = requestId;
                            sendMessageToKnowledge({
                                ...fromMessageModeToMessageOutput({
                                    message: message || chatInputValue,
                                    mode: CHAT_MODE.knowledge,
                                    conversationId: uploadedFiles.conversation_id ?? conversationId,
                                    hasFile: true,
                                    agentId: agentIdToUse,
                                    userAlias: typeof userNickname === 'string' ? userNickname : undefined,
                                    is_impersonated: isImpersonated,
                                    mindy_version: 'v2',
                                    impersonated_user_email: userInfo?.originalUserData?.email,
                                    original_user_id: userInfo?.originalUserData?.id,
                                    source: source,
                                    knowledgeDestination: knowledgeEmbeddingDestination,
                                }),
                                request_id: requestId,
                            });
                        },
                    },
                );
            } else if (currentMode === CHAT_MODE.logistics) {
                // QS-19310: el contexto de pantalla sólo viaja desde el widget embebido.
                // El botón "Continuar en Mindy" saca al usuario del SaaS hacia la app completa,
                // que ya no está dentro de ninguna pantalla — ahí `localStorage` tendría el
                // contexto de la última pantalla vista y respondería como si el usuario
                // siguiera en ella, así que no se adjunta fuera de source === 'embedded'.
                const saasQueryContext = source === SOURCE.EMBEDDED ? viewContextRef.current ?? undefined : undefined;

                if (agentVersion === 'v3') {
                    handleOptimisticUserRequest(message || chatInputValue);

                    startStream({
                        method: 'POST',
                        body: {
                            conversation_id: data?.metadata?.conversation_id || conversationId,
                            query: message || chatInputValue,
                            mindy_version: agentVersion,
                            mode: CHAT_MODE.logistics,
                            agent_id: agentIdToUse,
                            user_alias: typeof userNickname === 'string' ? userNickname : undefined,
                            is_impersonated: isImpersonated,
                            impersonated_user_email: userInfo?.originalUserData?.email,
                            original_user_id: userInfo?.originalUserData?.id,
                            source: source,
                            query_context: saasQueryContext,
                        },
                        onMessage: onStreamMessage,
                    });
                } else {
                    sendMessageToLogistics(
                        fromMessageModeToMessageOutput({
                            message: message || chatInputValue,
                            mode: CHAT_MODE.logistics,
                            conversationId: conversationId,
                            hasFile: hasFile,
                            agentId: agentIdToUse,
                            userAlias: typeof userNickname === 'string' ? userNickname : undefined,
                            is_impersonated: isImpersonated,
                            mindy_version: agentVersion,
                            impersonated_user_email: userInfo?.originalUserData?.email,
                            original_user_id: userInfo?.originalUserData?.id,
                            source: source,
                            query_context: saasQueryContext,
                        }),
                    );
                }
            } else {
                const requestId = generateLocalId();
                activeKnowledgeRequestIdRef.current = requestId;
                sendMessageToKnowledge({
                    ...fromMessageModeToMessageOutput({
                        message: message || chatInputValue,
                        mode: CHAT_MODE.knowledge,
                        conversationId: conversationId,
                        userAlias: typeof userNickname === 'string' ? userNickname : undefined,
                        is_impersonated: isImpersonated,
                        mindy_version: 'v2',
                        impersonated_user_email: userInfo?.originalUserData?.email,
                        original_user_id: userInfo?.originalUserData?.id,
                        source: source,
                        knowledgeDestination: knowledgeEmbeddingDestination,
                    }),
                    request_id: requestId,
                });
            }
        },
        [
            chatInputValue,
            files,
            userInfo,
            uploadFile,
            conversationId,
            hasFile,
            sendMessageToKnowledge,
            agentIdToUse,
            userNickname,
            isImpersonated,
            knowledgeEmbeddingDestination,
            userInfo?.originalUserData,
            setConversationId,
            setHasFile,
            agentVersion,
            handleOptimisticUserRequest,
            startStream,
            data?.metadata?.conversation_id,
            onStreamMessage,
            sendMessageToLogistics,
            viewContextRef,
        ],
    );
    const isLoading = isLoadingKnowledgeResponse || isLoadingLogisticsResponse || isUploadingFiles;

    const handleCancelStream = useCallback(() => {
        // Knowledge requests can be launched while the composer still shows logistics
        // (for example when attachments force a knowledge query), so use the pending
        // request id as the source of truth for the cancel path.
        const requestToCancel = activeKnowledgeRequestIdRef.current ?? activeKnowledgeRequestId ?? undefined;

        if (requestToCancel) {
            clearPendingThinkingMessages();
            void cancelKnowledgeRequest(requestToCancel).finally(() => {
                activeKnowledgeRequestIdRef.current = null;
            });
            return;
        }

        void cancelStream().finally(() => {
            clearPendingThinkingMessages();
        });
    }, [cancelKnowledgeRequest, cancelStream, clearPendingThinkingMessages, activeKnowledgeRequestId]);

    const shrinkPaddingToFit = useCallback(() => {
        requestAnimationFrame(() => {
            const container = chatContainerRef.current;
            const list = messagesListRef.current;
            if (!container || !list) return;

            const lastChild = list.lastElementChild;
            if (!lastChild) return;

            const currentScroll = window.scrollY;

            // If not scrolled (first message), use minimal padding
            if (currentScroll <= 10) {
                container.style.paddingBottom = '200px';
                return;
            }

            // Calculate exact padding to maintain current scroll position
            const containerRect = container.getBoundingClientRect();
            const lastChildRect = lastChild.getBoundingClientRect();
            const contentBottom = lastChildRect.bottom - containerRect.top;
            const viewportHeight = window.innerHeight;
            const containerTop = containerRect.top + currentScroll;
            const minPadding = Math.max(200, currentScroll + viewportHeight - containerTop - contentBottom + 50);

            container.style.paddingBottom = `${minPadding}px`;
        });
    }, [chatContainerRef]);

    useEffect(() => {
        setIsPending(isLoading);
        // Release scroll lock when loading ends (v2 flow)
        if (!isLoading) {
            releaseScrollLock();
            shrinkPaddingToFit();
        } else if (chatContainerRef.current) {
            chatContainerRef.current.style.paddingBottom = '';
        }
    }, [isLoading, setIsPending, shrinkPaddingToFit, chatContainerRef, releaseScrollLock]);

    useEffect(() => {
        setIsStream(isStreaming);
        // When streaming ends, shrink padding to fit (same as v2 flow)
        if (!isStreaming) {
            releaseScrollLock();
            shrinkPaddingToFit();
        } else if (chatContainerRef.current) {
            chatContainerRef.current.style.paddingBottom = '';
        }
    }, [isStreaming, setIsStream, shrinkPaddingToFit, chatContainerRef, releaseScrollLock]);

    useEffect(() => {
        if (agentVersion === 'v3' && data?.metadata?.conversation_id) {
            setConversationId(data.metadata.conversation_id);
            queryClient.invalidateQueries({ queryKey: ['conversations'] });
            queryClient.invalidateQueries({ queryKey: ['conversation', data.metadata.conversation_id] });
        }
    }, [agentVersion, data?.metadata?.conversation_id, setConversationId, queryClient]);

    useEffect(() => {
        if (!showKnowledgeEmbeddingSelector && knowledgeEmbeddingDestination !== UploadDestination.STANDARD) {
            setKnowledgeEmbeddingDestination(UploadDestination.STANDARD);
        }
    }, [knowledgeEmbeddingDestination, showKnowledgeEmbeddingSelector]);

    useEffect(() => {
        if (reset) {
            stopKnowledgeThinking();
            knowledgeThinkingIdRef.current = null;
            knowledgeThinkingStageIndexRef.current = -1;
            setMessages([]);
            setChatInputValue('');
            setFiles([]);
            setReset(false);
            setLoad(false);
            setHasFile(false);
            setConversationId(undefined);
            setMode(CHAT_MODE.logistics);
            setKnowledgeEmbeddingDestination(UploadDestination.STANDARD);
            clearData();
            // Reset streaming refs
            activeStreamIdRef.current = null;
            thinkingIdRef.current = null;
            streamBufferRef.current = '';
            activeKnowledgeRequestIdRef.current = null;
        }
    }, [reset, setConversationId, setHasFile, setMode, setReset, clearData, setLoad, stopKnowledgeThinking]);

    useEffect(() => {
        return () => {
            stopKnowledgeThinking();
            knowledgeThinkingIdRef.current = null;
        };
    }, [stopKnowledgeThinking]);

    useEffect(() => {
        if (reset) return;

        // Handle loading conversation from URL params
        if (load) {
            setMessages([{ text: t('chat.loadingOldChatMessage'), sender: SENDER.agent }]);
            setChatInputValue('');
            setFiles([]);
            if (loadedConversation && !isPending) {
                const loadedMessages =
                    loadedConversation?.interactions?.flatMap((interaction: ApiInteraction) =>
                        fromInteractionToConversationType(interaction),
                    ) || [];
                setMessages(loadedMessages);

                setMode(loadedConversationMode);

                setLoad(false);

                // Scroll to last user message when loading conversation
                if (loadedMessages.length > 0 && chatContainerRef.current) {
                    chatContainerRef.current.style.paddingBottom = '220px';
                    // Wait longer for DOM to render and force scroll
                    setTimeout(() => {
                        requestAnimationFrame(() => {
                            const messagesList = messagesListRef.current;
                            if (!messagesList) return;
                            const userMessages = messagesList.querySelectorAll('[data-sender="user"]');
                            const lastUserMsg = userMessages[userMessages.length - 1];
                            if (lastUserMsg) {
                                const rect = lastUserMsg.getBoundingClientRect();
                                const offset = 80;
                                const targetScrollY = window.scrollY + rect.top - offset;
                                const finalTarget = Math.max(0, targetScrollY);

                                // Smooth scroll with custom duration
                                const startY = window.scrollY;
                                const distance = finalTarget - startY;
                                const duration = 300;
                                let startTime: number | null = null;

                                const animateScroll = (currentTime: number) => {
                                    if (startTime === null) startTime = currentTime;
                                    const timeElapsed = currentTime - startTime;
                                    const progress = Math.min(timeElapsed / duration, 1);

                                    // Easing function (ease-out)
                                    const ease = 1 - Math.pow(1 - progress, 3);

                                    window.scrollTo(0, startY + distance * ease);

                                    if (progress < 1) {
                                        requestAnimationFrame(animateScroll);
                                    }
                                };

                                requestAnimationFrame(animateScroll);
                            }
                        });
                    }, 300);
                }
            }
            if (isErroGettingConversation) {
                setLoad(false);
            }
        }
        // Handle refetched conversation data (after agent responds)
        // Skip while streaming to avoid overwriting optimistic messages mid-stream
        else if (loadedConversation && !isPending && conversationId && !isStreamingRef.current) {
            const loadedMessages =
                loadedConversation?.interactions?.flatMap((interaction: ApiInteraction) =>
                    fromInteractionToConversationType(interaction),
                ) || [];

            setMode(loadedConversationMode);

            setMessages((prevMessages) => {
                // Check if update is needed
                const hasMessagesWithoutIds = prevMessages.some((msg) => !msg.interactionId);
                const hasMoreBackendMessages =
                    loadedMessages.length > prevMessages.filter((msg) => msg.interactionId).length;

                // Keep current state if no benefit (preserves optimistic updates)
                if (!hasMessagesWithoutIds && !hasMoreBackendMessages) {
                    return prevMessages;
                }

                // Smart merge: Replace optimistic messages with real backend data
                const backendMessageCount = loadedMessages.length;
                const currentMessagesWithIds = prevMessages.filter((msg) => msg.interactionId).length;

                if (backendMessageCount > currentMessagesWithIds) {
                    return compareMessages(loadedMessages, prevMessages);
                } else {
                    // Backend doesn't have the new interaction yet — preserve all local-only messages
                    // (both user message and agent response) so nothing disappears mid-flight
                    const optimisticMessages = prevMessages.filter((msg) => !msg.interactionId);
                    return [...loadedMessages, ...optimisticMessages];
                }
            });
        }
    }, [
        conversationId,
        isErroGettingConversation,
        isStreaming,
        isPending,
        load,
        loadedConversation,
        loadedConversationMode,
        setLoad,
        setMode,
        t,
        reset,
    ]);

    useEffect(() => {
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focusTextArea();
            }
        }, 100);
    }, [conversationId, mode, isStreaming, isLoading]);

    useEffect(() => {
        if (paramId !== undefined && paramId !== conversationId && !reset) {
            setConversationId(paramId);
            if (messages.length === 0) {
                setLoad(true);
            }
        }
    }, [conversationId, paramId, setConversationId, setLoad, messages.length, reset]);

    useEffect(() => {
        if (!load && !conversationId && !paramId) {
            setMode(knowledgeUser ? CHAT_MODE.knowledge : CHAT_MODE.logistics);
        }
    }, [conversationId, knowledgeUser, load, paramId, setMode]);

    useEffect(() => {
        // handle toast error messages
        if (!toasts.some((toast) => toast.title === t('chat.error.load.title')) && isErroGettingConversation) {
            toast({
                title: t('chat.error.load.title'),
                description: t('chat.error.load.description'),
                variant: 'destructive',
            });
        }
    }, [isErroGettingConversation, t, toast, toasts]);

    useEffect(() => {
        if (user && !firebaseIdToken) {
            user.getIdToken()
                .then((token) => {
                    setFirebaseIdToken(token);
                })
                .catch((error) => {
                    console.error('Error getting Firebase ID token:', error);
                });
        }
    }, [firebaseIdToken, user]);

    useEffect(() => {
        const timer = setTimeout(() => setShowDisclaimer(true), 2000);
        return () => clearTimeout(timer);
    }, []);

    // Track scroll position to determine "stickiness"
    useEffect(() => {
        const handleScroll = () => {
            const hasScroll = document.documentElement.scrollHeight > window.innerHeight;
            if (!hasScroll) {
                // Respect scroll lock: don't re-enable sticky if user just sent a message
                if (!scrollLockRef.current) {
                    isStickyRef.current = true;
                }
                setShowRestScrollButton(false);
                return;
            }

            const maxScrollY = document.documentElement.scrollHeight - globalThis.innerHeight;
            const distance = maxScrollY - globalThis.scrollY;

            // "Sticky" if we are within a threshold of the bottom (more permissive for better UX)
            const isAtBottom = distance <= 300;

            // While scroll is locked, keep sticky disabled.
            // The lock prevents the scroll handler from re-enabling sticky mode automatically.
            // We DO NOT unlock just because the user is at the bottom, to prevent
            // accidental unlocking when the message is the last one (and thus at bottom).
            if (scrollLockRef.current) {
                isStickyRef.current = false;
            } else {
                isStickyRef.current = isAtBottom;
            }

            // Show button if we are NOT at the bottom
            setShowRestScrollButton(!isAtBottom);

            if (inputContainerRef.current && chatContainerRef.current) {
                const inputRect = inputContainerRef.current.getBoundingClientRect();
                const chatRect = chatContainerRef.current.getBoundingClientRect();
                setButtonTop(inputRect.top - chatRect.top - 48 - 120);
            }
        };

        globalThis.addEventListener('scroll', handleScroll, { passive: true });
        globalThis.addEventListener('resize', handleScroll);

        // Initial check
        handleScroll();

        return () => {
            globalThis.removeEventListener('scroll', handleScroll);
            globalThis.removeEventListener('resize', handleScroll);
        };
    }, [chatContainerRef, inputContainerRef]);

    const hasKnowledgeConversation = useMemo(
        () => loadedConversationMode === CHAT_MODE.knowledge || cachedConversationMode === CHAT_MODE.knowledge,
        [cachedConversationMode, loadedConversationMode],
    );

    const isDifferentAgentVersion = useMemo(
        () =>
            shouldShowAgentVersionMismatch({
                mode,
                conversationAgentVersion,
                currentAgentVersion: agentVersion,
                isKnowledge: hasKnowledgeConversation,
            }),
        [conversationAgentVersion, agentVersion, hasKnowledgeConversation, mode],
    );

    const processMessageQueue = useCallback(() => {
        if (processingQueueRef.current || messageQueueRef.current.length === 0) {
            return;
        }

        processingQueueRef.current = true;
        const currentTime = Date.now();
        const timeSinceLastMessage = currentTime - lastMessageTimeRef.current;
        const delay = Math.max(0, 2000 - timeSinceLastMessage);

        setTimeout(() => {
            const queuedData = messageQueueRef.current.shift();

            if (queuedData?.message) {
                lastMessageTimeRef.current = Date.now();

                setMessages((prevMessages) => {
                    const lastMessage = prevMessages[prevMessages.length - 1];
                    if (
                        lastMessage?.sender === SENDER.agent &&
                        lastMessage.messageType === 'thinking' &&
                        !lastMessage.interactionId
                    ) {
                        return [
                            ...prevMessages.slice(0, -1),
                            {
                                ...lastMessage,
                                text: queuedData.message ?? '',
                                animated: true,
                            },
                        ];
                    } else {
                        return [
                            ...prevMessages,
                            {
                                text: queuedData.message ?? '',
                                sender: SENDER.agent,
                                animated: true,
                                messageType: 'thinking',
                                localId: generateLocalId(),
                            },
                        ];
                    }
                });
            }

            processingQueueRef.current = false;

            if (messageQueueRef.current.length > 0) {
                processMessageQueue();
            }
        }, delay);
    }, [generateLocalId]);

    const scrollToUserMessage = useCallback((behavior: ScrollBehavior = 'smooth') => {
        requestAnimationFrame(() => {
            const messagesList = messagesListRef.current;
            if (!messagesList) return;
            const userMessages = messagesList.querySelectorAll('[data-sender="user"]');
            const lastUserMsg = userMessages[userMessages.length - 1];
            if (lastUserMsg) {
                // First message is already naturally positioned at the top, no scroll needed
                if (userMessages.length <= 1) return;

                const rect = lastUserMsg.getBoundingClientRect();
                // Offset to clear the fixed header + breathing room so the question doesn't feel cramped
                const offset = 80;
                const targetScrollY = window.scrollY + rect.top - offset;

                targetUserMessageScrollPos.current = Math.max(0, targetScrollY);

                window.scrollTo({ top: targetUserMessageScrollPos.current, behavior });
            }
        });
    }, []);

    useEffect(() => {
        if (messages.length === 0) return;

        const lastMessage = messages[messages.length - 1];
        const isUserMessage = lastMessage?.sender === SENDER.user;

        if (isUserMessage) {
            scrollLockRef.current = true;
            targetUserMessageScrollPos.current = null;
            isStickyRef.current = false;
            // Reset padding to CSS class (80vh) for new question
            if (chatContainerRef.current) {
                chatContainerRef.current.style.paddingBottom = '';
            }
            setTimeout(() => {
                scrollToUserMessage('smooth');
            }, 300);
        } else if (isStickyRef.current) {
            setTimeout(() => {
                scrollToBottom('auto');
                isStickyRef.current = true;
            }, 50);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages.length, scrollToBottom, scrollToUserMessage]);

    useEffect(() => {
        const bottomElement = bottomRef.current;
        const listElement = messagesListRef.current;

        if (!bottomElement || !listElement) return;

        const observer = new ResizeObserver(() => {
            if (scrollLockRef.current && targetUserMessageScrollPos.current !== null) {
                window.scrollTo({ top: targetUserMessageScrollPos.current, behavior: 'auto' });
            } else if (isStickyRef.current) {
                scrollToBottom('auto');
            }
        });

        observer.observe(listElement);

        return () => observer.disconnect();
    }, [scrollToBottom]);

    const thinkingStages = useMemo(
        () => [
            { minSeconds: 1, message: 'Comprendiendo contexto...' },
            { minSeconds: 2, message: 'Procesando consulta...' },
            { minSeconds: 5, message: 'Consultando información logística...' },
            { minSeconds: 10, message: 'Estructurando la respuesta...' },
            { minSeconds: 15, message: 'Optimizando la respuesta...' },
            {
                minSeconds: 30,
                message: 'Esta respuesta está tardando más de lo esperado, por favor aguarde un momento...',
            },
        ],
        [],
    );

    const [thinkingStageIndex, setThinkingStageIndex] = useState(0);
    const loadingStartTimeRef = useRef<number | null>(null);
    const thinkingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        if (isLoading) {
            // Reset to first stage and record start time
            setThinkingStageIndex(0);
            loadingStartTimeRef.current = Date.now();

            // Check elapsed time every 500ms for smooth transitions
            thinkingIntervalRef.current = setInterval(() => {
                if (!loadingStartTimeRef.current) return;
                const elapsedSeconds = (Date.now() - loadingStartTimeRef.current) / 1000;

                // Find the highest stage whose minSeconds has been reached
                let newIndex = 0;
                for (let i = thinkingStages.length - 1; i >= 0; i--) {
                    if (elapsedSeconds >= thinkingStages[i].minSeconds) {
                        newIndex = i;
                        break;
                    }
                }
                setThinkingStageIndex(newIndex);
            }, 500);
        } else {
            // Cleanup when loading ends
            loadingStartTimeRef.current = null;
            if (thinkingIntervalRef.current) {
                clearInterval(thinkingIntervalRef.current);
                thinkingIntervalRef.current = null;
            }
            setThinkingStageIndex(0);
        }

        return () => {
            if (thinkingIntervalRef.current) {
                clearInterval(thinkingIntervalRef.current);
                thinkingIntervalRef.current = null;
            }
        };
    }, [isLoading, thinkingStages]);

    const progressiveThinkingMessage = thinkingStages[thinkingStageIndex]?.message ?? thinkingStages[0].message;

    const isModeLocked = useCallback(
        () => messages.length > 0 && agentVersion === 'v3',
        [messages.length, agentVersion],
    );

    const defaultSuggestedQuestionTitles = useMemo(
        () => suggestedQuestionsTemplate.map((q) => q.title),
        [suggestedQuestionsTemplate],
    );

    const handleSendLogisticsCard = useCallback(
        (msg: string) => handleSendMessage({ message: msg, mode: CHAT_MODE.logistics, source: SOURCE.WEB }),
        [handleSendMessage],
    );

    return {
        chatContainerRef,
        inputContainerRef,
        messagesListRef, // Export this ref
        firebaseIdToken,
        setFirebaseIdToken,
        paramId,
        toast,
        toasts,
        DEFAULT_QUESTIONS_QUANTITY,
        userInfo,
        user,
        mode,
        showKnowledgeEmbeddingSelector,
        knowledgeEmbeddingDestination,
        setKnowledgeEmbeddingDestination,
        conversationId,
        reset,
        load,
        hasFile,
        setHasFile,
        setLoad,
        setReset,
        setConversationId,
        ref,
        refFocusControl,
        inputRef,
        t,
        chatInputValue,
        setChatInputValue,
        files,
        setFiles,
        messages,
        setMessages,
        showDisclaimer,
        setShowDisclaimer,
        queryClient,
        suggestedQuestionsTemplate,
        dynamicSuggestedQuestions,
        availableUserAgents,
        suggestedQuestionsData,
        suggestedQuestionsQuantity,
        bgImageUrl,
        userNickname,
        currentOrganization,
        suggestedQuestions,
        customBackgroundImage,
        agentIdToUse,
        handleResetFinalAndInput,
        handleOptimisticUserRequest,
        handleOptimisticAgentResponse,
        loadedConversation,
        isPending,
        isErroGettingConversation,
        sendMessageToKnowledge,
        isLoadingKnowledgeResponse,
        activeKnowledgeRequestId,
        uploadFile,
        isUploadingFiles,
        isActive,
        sendMessageToLogistics,
        isLoadingLogisticsResponse,
        defaultSuggestedQuestionTitles,
        handleSendLogisticsCard,
        handleSendMessage,
        isLoading,
        showRestScrollButton,
        buttonTop,
        isDifferentAgentVersion,
        agentVersion,
        isStreaming,
        bottomRef,
        progressiveThinkingMessage,
        isModeLocked,
        scrollToBottomAndUnlock,
        handleCancelStream,
    };
};

export default useChat;
